import { useState } from 'react';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  IdentityLookupRow,
  IdentityLookupUploadedImage,
} from '../../api/identityVerificationDashboardApi';
import { resolveDocumentAuthenticityOutcome } from '../../lib/identityEvidenceQuestions';
import {
  ImagePreviewDialog,
  UploadedImagePreview,
} from '../../pages/IdentityVerificationLookup';
import { ApplicationSectionCard } from './applicationSectionChrome';

const UPLOAD_ORDER: Record<string, number> = {
  id_front: 0,
  id_back: 1,
  selfie: 2,
};

function uploadLabel(imageType: string): string {
  switch (imageType) {
    case 'id_front':
      return 'ID front';
    case 'id_back':
      return 'ID back';
    case 'selfie':
      return 'Selfie';
    default:
      return imageType;
  }
}

function toneChip(
  tone: 'success' | 'warning' | 'error' | 'info' | 'default' | undefined,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (tone === 'success' || tone === 'warning' || tone === 'error' || tone === 'info') return tone;
  return 'default';
}

/**
 * Document authenticity when Mitek actually ran — outcome + images submitted for verification.
 * Shown for new, duplicate, and returning re-verify paths alike.
 */
export function ApplicationMitekVerificationPanel({
  row,
}: {
  row: IdentityLookupRow;
}) {
  const theme = useTheme();
  const outcome = resolveDocumentAuthenticityOutcome(row);
  const [zoom, setZoom] = useState<{
    image: IdentityLookupUploadedImage;
    url: string;
    contentType: string;
  } | null>(null);

  if (!outcome || !row.mitekCalled) return null;

  const images = [...(row.uploadedImages ?? [])].sort((a, b) => {
    const ao = UPLOAD_ORDER[a.image_type] ?? 50;
    const bo = UPLOAD_ORDER[b.image_type] ?? 50;
    if (ao !== bo) return ao - bo;
    return a.sequence_index - b.sequence_index;
  });

  const failedChecks = (row.dossier?.verifications ?? [])
    .filter((v) => String(v.judgementGroup ?? '').toUpperCase() === 'FAILED')
    .map((v) => v.name)
    .filter((n): n is string => Boolean(n?.trim()));

  const accent =
    outcome.tone === 'error'
      ? theme.palette.error.main
      : outcome.tone === 'warning'
        ? theme.palette.warning.main
        : outcome.tone === 'success'
          ? theme.palette.success.main
          : theme.palette.info.main;

  return (
    <>
      <ApplicationSectionCard
        title="Document authenticity"
        subtitle="Mitek verification on the ID images submitted for this attempt"
        icon={<VerifiedUserRoundedIcon fontSize="small" />}
        endAdornment={
          <Chip
            size="small"
            color={toneChip(outcome.tone)}
            variant={outcome.tone === 'success' ? 'filled' : 'outlined'}
            label={outcome.answer}
            sx={{ height: 22, fontWeight: 700 }}
          />
        }
        data-testid={`application-mitek-panel-${row.iv_verification_id}`}
      >
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: `inset 3px 0 0 ${alpha(accent, 0.9)}`,
            bgcolor:
              theme.palette.mode === 'light'
                ? alpha(accent, 0.05)
                : alpha(accent, 0.1),
          }}
        >
          <Box sx={{ px: 1.5, py: 1.15 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45, fontSize: '0.8125rem', maxWidth: 720 }}
            >
              {outcome.body}
            </Typography>
            {failedChecks.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap sx={{ mt: 0.85 }}>
                {failedChecks.slice(0, 6).map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    color="error"
                    variant="outlined"
                    label={name}
                    sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>

          <Divider />

          <Box sx={{ px: 1.5, py: 1.15 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontSize: '0.62rem',
                display: 'block',
                mb: 0.85,
              }}
            >
              Images submitted for verification
            </Typography>
            {images.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                No upload audit rows are on file for this Mitek run.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                {images.map((img) => (
                  <Stack key={img.id} spacing={0.4} alignItems="center" sx={{ width: 104 }}>
                    <UploadedImagePreview
                      image={img}
                      size={100}
                      onClick={(url, contentType) => setZoom({ image: img, url, contentType })}
                    />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {uploadLabel(img.image_type)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </ApplicationSectionCard>
      <ImagePreviewDialog zoom={zoom} onClose={() => setZoom(null)} />
    </>
  );
}

/** Prefer a failed Mitek run; otherwise the latest attempt that called Mitek. */
export function pickMitekVerificationRows(rows: IdentityLookupRow[]): IdentityLookupRow[] {
  const withMitek = rows.filter((r) => r.mitekCalled);
  if (withMitek.length === 0) return [];
  const failed = withMitek.filter((r) => {
    const o = resolveDocumentAuthenticityOutcome(r);
    return o?.tone === 'error';
  });
  // Show each Mitek attempt once (failed first by original order among withMitek).
  const ordered = [...failed, ...withMitek.filter((r) => !failed.includes(r))];
  const seen = new Set<string>();
  const out: IdentityLookupRow[] = [];
  for (const r of ordered) {
    if (seen.has(r.iv_verification_id)) continue;
    seen.add(r.iv_verification_id);
    out.push(r);
  }
  return out;
}
